import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import { saveFiles, getCachedFiles } from "../lib/offline";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import DropZone from "../components/DropZone";
import RobotIcon from "../components/RobotIcon";
import InstallAppButton from "../components/InstallAppButton";
import { toast } from "sonner";

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDate(s) { try { return new Date(s).toLocaleString(); } catch { return s; } }

const FILE_COLORS = {
  pdf: "#EF4444", docx: "#2563EB", doc: "#2563EB", odt: "#2563EB", rtf: "#2563EB",
  xlsx: "#16A34A", xls: "#16A34A", ods: "#16A34A", csv: "#16A34A",
  pptx: "#F97316", ppt: "#F97316", odp: "#F97316",
  html: "#F97316", txt: "#52525B", md: "#52525B", zip: "#09090B",
  png: "#8B5CF6", jpg: "#8B5CF6", jpeg: "#8B5CF6", webp: "#8B5CF6", gif: "#8B5CF6", tiff: "#8B5CF6", bmp: "#8B5CF6",
};

const QUICK_ACTIONS = [
  { key: "ai_studio", to: "/tools/ai/studio", color: "#3E63DD",
    title_es: "AI Document Studio", title_en: "AI Document Studio",
    desc_es: "Chatea con tu PDF, resúmenes, extracción y traducción.",
    desc_en: "Chat with your PDF, summaries, extraction and translation.",
    robot: true,
    badge_es: "✦ Con IA", badge_en: "✦ With AI" },
  { key: "command", to: "/tools/ai/command", color: "#0EA5E9",
    title_es: "Comando natural", title_en: "Natural command",
    desc_es: 'Escribe "extrae 3-5, firma y conviértelo a Word".',
    desc_en: 'Type "extract 3-5, sign and convert to Word".',
    icon: "M4 6h16 M4 12h12 M4 18h8 M19 14l3 3-3 3" },
  { key: "convert", to: "/tools/convert", color: "#09090B",
    title_es: "Convertir", title_en: "Convert",
    desc_es: "PDF, Word, Excel, HTML, imágenes y más. Hasta varios a la vez.",
    desc_en: "PDF, Word, Excel, HTML, images and more. Even many at once.",
    icon: "M4 4h12l4 4v12H4z M16 4v4h4" },
  { key: "translate", to: "/tools/translate", color: "#3E63DD",
    title_es: "Traducir doc.", title_en: "Translate doc.",
    desc_es: "Cualquier formato → mismo formato, idioma elegido.",
    desc_en: "Any format → same format, language of your choice.",
    icon: "M4 6h6 M4 10h6 M7 4v2 M14 14h6 M14 18h6 M17 12v2 M10 4l-3 8h2l1-3h2l1 3h2L12 4z",
    badge_es: "✦ IA", badge_en: "✦ AI" },
  { key: "pdf", to: "/tools/pdf", color: "#EF4444",
    title_es: "Herramientas PDF", title_en: "PDF Tools",
    desc_es: "Fusionar, dividir, firmar, extraer páginas…",
    desc_en: "Merge, split, sign, extract pages…",
    icon: "M9 4h6v6h6v6h-6v4H9v-4H3v-6h6z",
    badge_es: "8 tools", badge_en: "8 tools" },
  { key: "docgen", to: "/tools/docgen", color: "#7C3AED",
    title_es: "Generador + 100 plantillas", title_en: "100+ templates",
    desc_es: "Contratos, despidos, actas, NDAs… con IA y membrete.",
    desc_en: "Contracts, dismissals, minutes, NDAs… AI-powered with letterhead.",
    icon: "M5 4h10l4 4v12H5z M9 12h6 M9 16h4 M14 4v4h4",
    badge_es: "✦ IA · +100", badge_en: "✦ AI · 100+" },
  { key: "scan", to: "/tools/scan", color: "#FFC53D",
    title_es: "Escanear documento", title_en: "Scan document",
    desc_es: "Haz fotos con la cámara y conviértelas en PDF (con OCR).",
    desc_en: "Snap photos and merge them into a PDF (with OCR).",
    icon: "M3 7V5a2 2 0 0 1 2-2h2 M17 3h2a2 2 0 0 1 2 2v2 M21 17v2a2 2 0 0 1-2 2h-2 M7 21H5a2 2 0 0 1-2-2v-2 M8 10h8 M8 14h5" },
  { key: "share", to: "/tools/share-sign", color: "#2563EB",
    title_es: "Firma compartible", title_en: "Sign link",
    desc_es: "Envía un enlace y firman sin registro.",
    desc_en: "Send a link, they sign without signup.",
    icon: "M4 12v5h16v-5 M12 4v10 M8 8l4-4 4 4" },
  { key: "recipes", to: "/tools/recipes", color: "#F59E0B",
    title_es: "Recetas", title_en: "Recipes",
    desc_es: "Encadena pasos y guárdalos para reutilizar.",
    desc_en: "Chain steps and save them to reuse.",
    icon: "M4 4h16v4H4z M4 10h16v4H4z M4 16h16v4H4z" },
];

const EXAMPLES_ES = [
  "Extrae las páginas 3 a 5 y conviértelas a Word",
  "Añade marca de agua «BORRADOR» y comprime al máximo",
  "Resume este documento en 5 puntos clave",
  "Traduce este PDF al inglés",
];
const EXAMPLES_EN = [
  "Extract pages 3 to 5 and convert to Word",
  "Add a 'DRAFT' watermark and compress to the max",
  "Summarize this document in 5 key bullets",
  "Translate this PDF to Spanish",
];

function StatTile({ label, value, accent, testId }) {
  return (
    <div className="border border-zinc-200 bg-white p-5 rounded-md flex flex-col" data-testid={testId}>
      <span className="eyebrow">{label}</span>
      <span className="hero-display text-4xl mt-2" style={{ color: accent || "#09090B" }}>{value}</span>
    </div>
  );
}

function FileTile({ f, onOpen, onAction }) {
  const color = FILE_COLORS[f.ext] || "#09090B";
  return (
    <div className="border border-zinc-200 hover:border-black bg-white rounded-md p-4 flex gap-4 items-center transition-all hover:-translate-y-0.5"
      data-testid={`file-tile-${f.id}`}>
      <div className="w-12 h-14 grid place-items-center rounded-sm shrink-0" style={{ background: color }}>
        <span className="mono text-[10px] font-bold text-white tracking-wider">.{f.ext}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate text-sm">{f.original_filename}</div>
        <div className="flex gap-2 mt-1 items-center">
          <span className="pill text-[10px]">{f.kind}</span>
          {f.operation && <span className="text-[11px] text-zinc-500 mono truncate">{f.operation}</span>}
        </div>
        <div className="text-[11px] text-zinc-400 mt-1">{fmtSize(f.size)} · {fmtDate(f.created_at)}</div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <Button variant="ghost" className="rounded-sm h-8 px-2 text-xs" onClick={() => onOpen(f)}
          data-testid={`tile-download-${f.id}`}>↓</Button>
        <Button variant="ghost" className="rounded-sm h-8 px-2 text-xs text-red-600 hover:text-red-700"
          onClick={() => onAction(f.id, "delete")} data-testid={`tile-delete-${f.id}`}>✕</Button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);

  const isES = i18n.language === "es";
  const examples = isES ? EXAMPLES_ES : EXAMPLES_EN;

  // Online/offline listeners
  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/files");
      setFiles(data);
      if (user?.id) saveFiles(user.id, data).catch(() => {});
      setOffline(!navigator.onLine);
    } catch (e) {
      // Network failed — fall back to IndexedDB cache
      if (user?.id) {
        const cached = await getCachedFiles(user.id);
        if (cached.length > 0) {
          setFiles(cached);
          setOffline(true);
          toast.message(isES ? "Sin conexión: archivos en caché" : "Offline: showing cached files");
          setLoading(false);
          return;
        }
      }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  const stats = useMemo(() => {
    const total = files.length;
    const converted = files.filter((f) => f.kind === "converted").length;
    const tools = files.filter((f) => f.kind === "tool_output").length;
    const ai = files.filter((f) => (f.operation || "").startsWith("ai_") || (f.operation || "").startsWith("translate_")).length;
    const totalBytes = files.reduce((s, f) => s + (f.size || 0), 0);
    return { total, converted, tools, ai, totalBytes };
  }, [files]);

  async function quickUpload(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd);
      toast.success(t("file_loaded"));
      await load();
      // Smart routing: if PDF → ask via UI options, otherwise jump to convert
      const ext = (data.ext || "").toLowerCase();
      if (ext === "pdf") {
        navigate(`/tools/ai/studio?file_id=${data.id}`);
      } else {
        navigate(`/tools/convert?file_id=${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setUploading(false);
  }

  async function onAction(id, action) {
    if (action === "delete") {
      try {
        await api.delete(`/files/${id}`);
        setFiles((f) => f.filter((x) => x.id !== id));
        toast.success(t("success"));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  }

  async function onOpen(f) {
    try { await downloadFile(f.id, f.original_filename); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  function greeting() {
    const h = new Date().getHours();
    if (isES) {
      if (h < 13) return "Buenos días";
      if (h < 21) return "Buenas tardes";
      return "Buenas noches";
    }
    if (h < 13) return "Good morning";
    if (h < 19) return "Good afternoon";
    return "Good evening";
  }

  function tryExample(text) {
    // Pick the most recent PDF file, otherwise just open command page
    const pdf = files.find((f) => f.ext === "pdf");
    const param = pdf ? `?file_id=${pdf.id}&q=${encodeURIComponent(text)}` : `?q=${encodeURIComponent(text)}`;
    navigate(`/tools/ai/command${param}`);
  }

  const recent = files.slice(0, 6);

  return (
    <div className="min-h-screen bg-zinc-50/40" data-testid="dashboard-page">
      {/* Hero greeting */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <span className="eyebrow">DocuConvert · Dashboard</span>
            <h1 className="hero-display text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="greeting">
              {greeting()},{" "}
              <span className="text-zinc-400">{user?.name?.split(" ")[0] || user?.email?.split("@")[0]}</span>.
            </h1>
            <p className="text-zinc-600 mt-3 font-light text-lg">
              {isES
                ? "Listo para trabajar. ¿Qué quieres hacer hoy?"
                : "Ready to work. What would you like to do today?"}
            </p>
            {offline && (
              <div className="mt-4 inline-flex items-center gap-2 border-2 border-[#1C1917] bg-[#FFC53D] px-3 py-1.5 shadow-[3px_3px_0_0_#1C1917]" data-testid="offline-banner">
                <span className="w-2 h-2 rounded-full bg-[#E5484D] inline-block" />
                <span className="text-xs font-bold tracking-wider uppercase">
                  {isES ? "Modo offline · mostrando caché" : "Offline · showing cache"}
                </span>
              </div>
            )}
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label={isES ? "Archivos" : "Files"} value={stats.total} testId="stat-total" />
            <StatTile label={isES ? "Conversiones" : "Conversions"} value={stats.converted} accent="#3E63DD" testId="stat-converted" />
            <StatTile label={isES ? "Herramientas" : "Tool runs"} value={stats.tools} accent="#E5484D" testId="stat-tools" />
            <StatTile label={isES ? "Con IA" : "AI uses"} value={stats.ai} accent="#3E63DD" testId="stat-ai" />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-12">
        {/* Quick upload + featured */}
        <section className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-3">
              <span className="eyebrow">01 · {isES ? "Subida rápida" : "Quick upload"}</span>
              <span className="text-xs text-zinc-500">{isES ? "Suelta un archivo y te llevamos al sitio justo" : "Drop a file and we'll route you to the right tool"}</span>
            </div>
            <DropZone
              accept=""
              onFiles={(fs) => quickUpload(fs[0])}
              label={isES ? "Suelta tu documento aquí" : "Drop your document here"}
              testId="dashboard-quickdrop"
            />
            {uploading && <div className="text-xs text-zinc-500 mt-2">{t("uploading")}</div>}
          </div>

          {/* Featured AI Studio card */}
          <Link
            to="/tools/ai/studio"
            className="relative overflow-hidden border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917] hover:shadow-[10px_10px_0_0_#1C1917] hover:-translate-x-[2px] hover:-translate-y-[2px] p-6 grain group transition-all bg-[#3E63DD]"
            data-testid="featured-ai-studio"
          >
            <div aria-hidden className="absolute -right-12 -bottom-12 w-44 h-44 border-2 border-white/30"
              style={{ background: "radial-gradient(circle, rgba(255,197,61,0.35), transparent 70%)" }} />
            <div className="relative">
              <span className="sticker">AI Studio</span>
              <div className="display text-3xl text-white mt-4">AI Document Studio</div>
              <p className="text-white/85 font-light mt-3 text-sm leading-relaxed">
                {isES
                  ? "Chatea con tu PDF, Word o Excel. Resúmenes, datos clave y traducción al instante."
                  : "Chat with your PDF, Word or Excel. Summaries, key data and translation, instantly."}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-white group-hover:gap-3 transition-all bg-[#1C1917] border-2 border-[#1C1917] px-3 py-1.5">
                <span>{isES ? "Probar" : "Try now"}</span>
                <span>→</span>
              </div>
            </div>
          </Link>
        </section>

        {/* Quick actions grid */}
        <section>
          <div className="flex items-end justify-between mb-5">
            <div>
              <span className="eyebrow">02 · {isES ? "Atajos" : "Shortcuts"}</span>
              <h2 className="hero-h2 text-2xl sm:text-3xl mt-1">{isES ? "Empieza al instante" : "Jump right in"}</h2>
            </div>
            <Link to="/tools/pdf" className="text-xs uppercase tracking-wider underline underline-offset-4 hover:text-zinc-500"
              data-testid="dashboard-all-tools">
              → {isES ? "Ver todo" : "See all"}
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {QUICK_ACTIONS.map((a, i) => (
              <Link
                key={a.key}
                to={a.to}
                className="tool-card p-4 rounded-md flex flex-col gap-3 fade-up min-h-[160px]"
                style={{ animationDelay: `${i * 40}ms` }}
                data-testid={`shortcut-${a.key}`}
              >
                <div className="flex justify-between items-start">
                  <div className="w-10 h-10 grid place-items-center rounded-sm" style={{ background: a.color }}>
                    {a.robot ? (
                      <RobotIcon size={20} stroke="white" strokeWidth={2.2} fill="white" />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d={a.icon} />
                      </svg>
                    )}
                  </div>
                  {(isES ? a.badge_es : a.badge_en) && (
                    <span className="text-[10px] tracking-wider uppercase font-medium text-zinc-500">
                      {isES ? a.badge_es : a.badge_en}
                    </span>
                  )}
                </div>
                <div className="font-bold text-sm leading-tight">{isES ? a.title_es : a.title_en}</div>
                <div className="text-[11px] text-zinc-500 leading-relaxed">{isES ? a.desc_es : a.desc_en}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Try this — AI command examples */}
        <section className="border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917] p-6 bg-white">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <span className="eyebrow">03 · {isES ? "Pruébalo" : "Try this"}</span>
              <h2 className="hero-h2 text-xl sm:text-2xl mt-1">
                {isES ? "Dile a la IA lo que quieres" : "Tell the AI what you want"}
              </h2>
            </div>
            <Link to="/tools/ai/command" className="text-xs uppercase tracking-wider underline underline-offset-4">
              → {t("nc_run")}
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {examples.map((ex, i) => (
              <button
                key={i}
                onClick={() => tryExample(ex)}
                className="text-left border border-zinc-200 hover:border-black rounded-md p-4 transition-all hover:-translate-y-0.5 bg-zinc-50 hover:bg-white"
                data-testid={`example-${i}`}
              >
                <span className="text-[10px] tracking-wider uppercase font-medium text-violet-600">
                  ✨ {isES ? "Ejemplo" : "Example"}
                </span>
                <div className="mt-2 text-sm leading-snug">"{ex}"</div>
                <div className="mt-3 text-xs text-zinc-500 group-hover:text-black flex items-center gap-1">
                  <span>{isES ? "Probar" : "Try"}</span> →
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Recent activity */}
        <section>
          <div className="flex items-end justify-between mb-5">
            <div>
              <span className="eyebrow">04 · {isES ? "Reciente" : "Recent"}</span>
              <h2 className="hero-h2 text-2xl sm:text-3xl mt-1">{isES ? "Tu actividad" : "Your activity"}</h2>
            </div>
            <span className="text-xs text-zinc-500">
              {stats.total} {isES ? "archivos" : "files"} · {fmtSize(stats.totalBytes)}
            </span>
          </div>

          {loading && <div className="text-zinc-500 text-sm">{t("auth_loading")}</div>}

          {!loading && files.length === 0 && (
            <div className="border-2 border-dashed border-zinc-200 rounded-md p-12 text-center">
              <div className="hero-display text-5xl text-zinc-300">∅</div>
              <p className="mt-4 text-zinc-500">{t("dashboard_empty")}</p>
              <Button onClick={() => navigate("/tools/convert")}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 mt-6 h-11 px-6"
                data-testid="empty-cta">
                {isES ? "Convertir mi primer archivo" : "Convert my first file"} →
              </Button>
            </div>
          )}

          {!loading && recent.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {recent.map((f) => (
                  <FileTile key={f.id} f={f} onOpen={onOpen} onAction={onAction} />
                ))}
              </div>
              {files.length > recent.length && (
                <div className="mt-6 text-center">
                  <details className="inline-block">
                    <summary className="cursor-pointer text-xs uppercase tracking-wider underline underline-offset-4 hover:text-zinc-500">
                      {isES ? `Ver los ${files.length - recent.length} restantes` : `Show remaining ${files.length - recent.length}`}
                    </summary>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                      {files.slice(recent.length).map((f) => (
                        <FileTile key={f.id} f={f} onOpen={onOpen} onAction={onAction} />
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Floating Scan FAB */}
      <Link
        to="/tools/scan"
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-40 inline-flex items-center gap-3 h-14 sm:h-16 px-5 sm:px-6 border-2 border-[#1C1917] bg-[#FFC53D] text-[#1C1917] font-black text-xs sm:text-sm tracking-widest uppercase shadow-[4px_4px_0_0_#1C1917] sm:shadow-[6px_6px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_0_#1C1917] transition-transform"
        data-testid="fab-scan"
        aria-label={isES ? "Escanear documento" : "Scan document"}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <span className="hidden sm:inline">{isES ? "Escanear" : "Scan"}</span>
      </Link>
    </div>
  );
}
